/**
 * GameEventTypes.ts - 完整的游戏事件类型定义
 * 基于核心功能分析，包含统一ID设计和完整数据结构
 */

// ==================== 基础类型定义 ====================

export interface Position {
  x: number;
  y: number;
}

export interface Health {
  current: number;
  max: number;
}

export interface GameContext {
  gameId: string;
  turnNumber: number;
  phase: 'INITIALIZATION' | 'AGENT_ACTIONS' | 'COMBAT_RESOLUTION' | 'ENVIRONMENT_UPDATE' | 'TURN_END';
  gameTime: number; // 游戏运行时间(ms)
  remainingAgents: number;
  safeZoneRadius: number;
}

export interface EventMetadata {
  triggeredBy?: string; // 谁触发的事件
  relatedEvents?: string[]; // 相关事件ID
  priority?: 'low' | 'normal' | 'high';
  category?: string;
  source?: 'system' | 'agent' | 'user' | 'llm';
}

// ==================== 标准事件接口 ====================

export interface GameEvent<T = any> {
  eventType: string;
  eventId: string;
  sequence: number;
  timestamp: string;
  data: T;
  context: GameContext;
  metadata?: EventMetadata;
}

// ==================== 🎯 游戏生命周期事件 ====================

export interface GameInitializedData {
  gameId: string;
  mapSize: { width: number; height: number };
  initialAgentCount: number;
  maxTurns: number;
  gameConfig: {
    safeZoneConfig: any;
    resourceConfig: any;
    combatConfig: any;
  };
}

export interface GameStartedData {
  gameId: string;
  startTime: string;
  initialState: {
    agents: AgentBasicInfo[];
    safeZone: { center: Position; radius: number };
    resources: ItemInfo[];
  };
}

export interface GameEndedData {
  gameId: string;
  endTime: string;
  duration: number; // 总时长(ms)
  totalTurns: number;
  endReason: 'VICTORY' | 'MAX_TURNS' | 'MANUAL_STOP' | 'ERROR';
  winners: string[]; // 胜利者ID列表
  finalRanking: Array<{
    agentId: string;
    rank: number;
    survivalTime: number;
    finalScore: number;
  }>;
  gameStatistics: {
    totalCombats: number;
    totalDamageDealt: number;
    totalResourcesCollected: number;
    averageTurnDuration: number;
  };
}

// ==================== 📅 回合系统事件 ====================

export interface TurnStartedData {
  turnNumber: number;
  maxTurns: number;
  turnId: string;
  startTime: string;
  remainingTurns: number;
  progress: number; // 0-100
  expectedDuration: number;
}

export interface TurnEndedData {
  turnNumber: number;
  turnId: string;
  duration: number;
  endTime: string;
  averageDuration: number;
  isGameComplete: boolean;
  nextTurnEstimate?: string;
}

export interface TurnPhaseChangedData {
  phase: GameContext['phase'];
  previousPhase: GameContext['phase'];
  turnNumber: number;
  phaseStartTime: string;
  estimatedDuration: number;
}

// ==================== 🤖 智能体事件 ====================

export interface AgentBasicInfo {
  agentId: string;
  name: string;
  // 放宽为字符串以兼容更丰富的前端展示类型（如 'slime' 等）
  type: string;
  position: Position;
  health: Health;
  status: 'alive' | 'injured' | 'dead' | 'eliminated';
}

export interface AgentSpawnedData extends AgentBasicInfo {
  // 可选的前端展示颜色（用于动画/材质选择）
  color?: string;
  spawnTime: string;
  initialAttributes: {
    strength: number;
    defense: number;
    speed: number;
  };
  initialInventory: ItemInfo[];
  spawnPosition: Position;
}

export interface AgentMovedData {
  agentId: string;
  from: Position;
  to: Position;
  moveType: 'walk' | 'run' | 'teleport';
  // 兼容前端 Bridge 的冗余字段（可选）
  fromPosition?: Position;
  toPosition?: Position;
  position?: Position; // 一般等于 to
  path?: Position[]; // 完整移动路径
  pathId?: string; // LLM规划的路径ID
  distance: number;
  movementCost: number; // 消耗的移动点数
  terrain: {
    from: string;
    to: string;
  };
}

export interface AgentHealthChangedData {
  agentId: string;
  previousHealth: Health;
  currentHealth: Health;
  changeAmount: number; // 正数为恢复，负数为伤害
  changeSource: 'combat' | 'healing' | 'safeZone' | 'environment' | 'item';
  sourceId?: string; // 伤害来源ID
  isHealthCritical: boolean; // 生命值是否危险
  healingItems?: string[]; // 使用的治疗物品
}

export interface AgentEliminatedData extends AgentBasicInfo {
  eliminationTime: string;
  elimination: {
    cause: 'combat' | 'safeZone' | 'environment' | 'timeout';
    eliminatedBy?: string;
    finalPosition: Position;
    finalHealth: number;
    survivalTime: number;
    survivalTurns: number;
  };
  statistics: {
    totalDamageDealt: number;
    totalDamageReceived: number;
    resourcesCollected: number;
    itemsUsed: number;
    combatsWon: number;
    combatsLost: number;
    distanceTraveled: number;
    finalRank: number;
    finalScore: number;
  };
}

// ==================== ⚔️ 战斗系统事件 ====================

export interface CombatStartedData {
  combatId: string;
  attackerId: string;
  defenderId: string;
  position: Position;
  terrain: string;
  startTime: string;
  attackerColor?: string;
  defenderColor?: string;
  attackerWeapon: ItemInfo | null;
  defenderWeapon: ItemInfo | null;
  attackerHealth: Health;
  defenderHealth: Health;
}

export interface CombatDamageDealtData {
  combatId: string;
  attackerId: string;
  defenderId: string;
  damage: number;
  weaponUsed: ItemInfo | null;
  attackType: 'normal' | 'critical' | 'counter';
  defenderRemainingHealth: Health;
  attackerRemainingHealth: Health;
  isCriticalHit: boolean;
  damageReduction: number; // 防御减免
  weaponDurabilityBefore: number;
  weaponDurabilityAfter: number;
  attackerColor?: string;
  defenderColor?: string;
}

export interface CombatEndedData {
  combatId: string;
  winner: string;
  loser: string;
  duration: number;
  totalTurns: number;
  finalHealths: {
    [agentId: string]: Health;
  };
  combatSummary: {
    totalDamageByWinner: number;
    totalDamageByLoser: number;
    decisiveFactor: 'weapon' | 'attributes' | 'terrain' | 'luck';
  };
  weaponChanges: {
    [agentId: string]: {
      weaponId: string;
      durabilityBefore: number;
      durabilityAfter: number;
      isBroken: boolean;
    };
  };
  segments?: {
    attackCount: number;
    missCount: number;
    total: number;
  };
  estimatedDurationMs?: number;
}

// ==================== 🎒 资源/库存系统事件 (统一ItemID) ====================

export interface ItemInfo {
  itemId: string; // 🔥 统一ID：全局唯一
  type: string;
  name: string;
  category: 'weapon' | 'consumable' | 'material' | 'equipment';
  status: 'spawned' | 'collected' | 'equipped' | 'consumed' | 'dropped' | 'broken';
  // 🔧 可选：武器的具体子类型（用于动画/显示）。当 category=weapon 时可能存在。
  weaponType?: import('../config/unified').WeaponType;
  position?: Position; // 在世界中的位置（如果未被收集）
  owner?: string; // 拥有者ID（如果被收集）
  properties: {
    durability?: number; // 耐久度（武器/装备）
    maxDurability?: number;
    healingPower?: number; // 治疗力（消耗品）
    attackPower?: number; // 攻击力（武器）
    defenseBonus?: number; // 防御加成（装备）
    stackSize?: number; // 堆叠数量
  };
}

export interface ResourceSpawnedData {
  spawnBatch: {
    batchId: string;
    spawnTime: string;
    spawnCount: number;
    turnNumber: number;
  };
  newItems: ItemInfo[]; // 新生成的物品列表
  totalItemsInWorld: number;
  spawnReason: 'turn_generation' | 'manual_spawn' | 'event_trigger';
}

export interface ResourceCollectedData {
  itemId: string; // 🔥 统一ID
  agentId: string;
  collectorName: string;
  item: ItemInfo; // 完整物品信息
  collectionTime: string;
  collectionPosition: Position;
  collectionSuccess: boolean;
  inventoryAfterCollection: ItemInfo[]; // 收集后的完整库存
  reason?: string; // 收集失败原因
}

// 🔥 新增：资源消失（系统清扫/过期/掩码对齐等），不代表“被玩家收集”
export interface ResourceDespawnedData {
  itemId: string;
  previousStatus: 'spawned' | 'collected' | 'equipped' | 'consumed' | 'dropped' | 'broken';
  category?: 'weapon' | 'consumable' | 'material' | 'equipment';
  type?: string; // 具体类型（如 SWORD/LANCE 或 small/large）
  position?: Position; // 消失前位置（如果存在）
  despawnTime: string;
  reason: 'system_cleanup' | 'expiry' | 'mask_reconcile' | 'room_reset' | 'manual';
}

export interface ItemUsedData {
  itemId: string; // 🔥 统一ID
  agentId: string;
  item: ItemInfo;
  useTime: string;
  usePosition: Position;
  useEffect: {
    type: 'healing' | 'attack' | 'defense' | 'utility';
    value: number;
    duration?: number; // 效果持续时间
  };
  wasConsumed: boolean; // 是否被消耗
  remainingUses?: number; // 剩余使用次数
  inventoryAfterUse: ItemInfo[];
}

// 🔥 新增：武器装备事件 - 处理角色变身逻辑
export interface WeaponEquippedData {
  weaponId: string; // 🔥 统一ID
  agentId: string;
  agentName: string;
  weapon: ItemInfo; // 装备的武器信息
  // 🔧 可选：为方便前端动画直接读取具体武器类型
  weaponType?: import('../config/unified').WeaponType;
  equipTime: string;
  equipPosition: Position;
  characterTransformation: {
    previousType: 'normal' | 'berserker' | 'swordmaster' | 'halberdier'; // 之前的角色类型
    newType: 'normal' | 'berserker' | 'swordmaster' | 'halberdier'; // 新的角色类型
    weaponType: 'SWORD' | 'AXE' | 'LANCE'; // 触发变身的武器类型
    visualAssetChange: {
      previousAsset: string; // 之前的素材路径
      newAsset: string; // 新的素材路径
    };
  };
  attributeChanges: {
    strength: number; // 力量变化
    defense: number; // 防御变化  
    speed: number; // 速度变化
  };
  newCapabilities: string[]; // 新获得的能力
  equipmentSlotChanges: {
    [slotType: string]: {
      oldItem?: ItemInfo;
      newItem: ItemInfo;
    };
  };
}

export interface WeaponDurabilityChangedData {
  weaponId: string; // 🔥 统一ID
  agentId: string;
  weapon: ItemInfo;
  durabilityBefore: number;
  durabilityAfter: number;
  changeReason: 'combat_use' | 'environmental_damage' | 'repair' | 'enhancement';
  isBroken: boolean;
  remainingUses: number; // 预估剩余使用次数
}

// ==================== 🌍 环境系统事件 ====================

export interface SafeZoneWarningData {
  currentTurn: number;
  warningType: 'contraction_warning';
  message: string;
  safeZone: {
    center: Position;
    currentRadius: number;
    nextRadius: number;
  };
  contraction: {
    scheduledTurn: number;
    turnsRemaining: number;
    estimatedTime: string;
    estimatedDuration: number;
  };
  recommendation: string;
}

export interface SafeZoneContractionStartedData {
  contractionId: string;
  startTurn: number;
  startTime: string;
  contraction: {
    from: {
      center: Position;
      radius: number;
    };
    to: {
      center: Position;
      radius: number;
    };
    contractionRate: number;
    estimatedDuration: number;
  };
  isPhaseTransition: boolean;
  newPhase: 'stable' | 'warning' | 'contracting' | 'final';
}

export interface SafeZoneContractionCompletedData {
  contractionId: string;
  completedTurn: number;
  completedTime: string;
  duration: number;
  safeZone: {
    center: Position;
    radius: number;
    phase: 'stable' | 'warning' | 'contracting' | 'final';
  };
  next: {
    contractionTurn: number | null;
    estimatedTime: string | null;
  };
  statistics: {
    radiusReduction: number;
    centerShift: number;
    affectedArea: number;
  };
}

export interface EnvironmentDamageData {
  damageId: string;
  source: 'safeZone' | 'terrain' | 'weather' | 'trap';
  affectedAgents: Array<{
    agentId: string;
    position: Position;
    damageAmount: number;
    healthBefore: Health;
    healthAfter: Health;
    isEliminated: boolean;
  }>;
  totalDamage: number;
  eliminatedAgents: string[];
  damageDetails: {
    sourcePosition?: Position;
    damageRadius?: number;
    damageType: string;
    mitigationFactors?: string[];
  };
}

export interface SafeZoneUpdatedData {
  center: Position;
  currentRadius: number;
  nextRadius: number;
  contractionProgress: number; // 0-100
  nextContraction: {
    startTurn: number;
    startTime: string;
    estimatedDuration: number;
  };
  phase: 'stable' | 'warning' | 'contracting' | 'final';
  affectedAgents: string[]; // 在危险区域的智能体
}

export interface SafeZoneDamageAppliedData {
  affectedAgents: Array<{
    agentId: string;
    position: Position;
    damageAmount: number;
    distanceFromSafeZone: number;
    healthAfterDamage: Health;
    isEliminated: boolean;
  }>;
  totalDamageDealt: number;
  agentsEliminated: string[];
  safeZoneStatus: {
    center: Position;
    radius: number;
  };
}

export interface MapGeneratedData {
  mapId: string;
  mapSize: { width: number; height: number };
  terrainMap: string[][]; // 地形类型矩阵
  obstacles: Array<{
    id: string;
    type: string;
    position: Position;
    size: { width: number; height: number };
  }>;
  spawnPoints: Position[];
  specialAreas: Array<{
    type: string;
    area: { x: number; y: number; width: number; height: number };
  }>;
  generationTime: string;
  seed?: number; // 地图种子
}

// ==================== 🧠 LLM决策系统事件 ====================

export interface LLMPathPlannedData {
  agentId: string;
  pathId: string;
  requestId: string;
  path: Position[];
  plannedActions: Array<{
    type: string;
    position: Position;
    target?: string;
    estimatedCost: number;
  }>;
  pathMetrics: {
    totalDistance: number;
    estimatedTurns: number;
    safetyScore: number; // 0-1
    efficiencyScore: number; // 0-1
  };
  llmAnalysis: {
    confidence: number; // 0-1
    reasoning: string;
    alternativePaths: number;
    riskAssessment: 'low' | 'medium' | 'high';
  };
}

export interface LLMDecisionMadeData {
  agentId: string;
  decisionId: string;
  requestId: string;
  chosenOption: {
    id: string;
    name: string;
    type: string;
    score: number;
    expectedOutcome: string;
  };
  allEvaluatedOptions: Array<{
    id: string;
    name: string;
    type: string;
    score: number;
    pros: string[];
    cons: string[];
  }>;
  decisionAnalysis: {
    confidence: number;
    reasoning: string;
    riskLevel: 'low' | 'medium' | 'high';
    expectedSuccess: number; // 0-1
  };
  pathId?: string;
  actionPlan: any[];
  estimatedExecutionTime: number;
}

// ==================== 🔧 系统管理事件 ====================

export interface SystemErrorData {
  errorId: string;
  errorType: 'runtime' | 'validation' | 'network' | 'llm' | 'database';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  stack?: string;
  context: {
    component: string;
    method: string;
    agentId?: string;
    gameState: string;
  };
  autoRecovery?: {
    attempted: boolean;
    successful: boolean;
    recoveryMethod: string;
  };
}

export interface SystemStateSyncData {
  syncId: string;
  syncType: 'full' | 'incremental' | 'agent_specific';
  syncTime: string;
  gameState: {
    currentTurn: number;
    phase: string;
    gameTime: number;
  };
  agents: AgentBasicInfo[];
  items: ItemInfo[];
  safeZone: {
    center: Position;
    radius: number;
  };
  recentEvents: string[]; // 最近事件ID列表
  checksums: {
    agents: string;
    items: string;
    gameState: string;
  }; // 数据完整性检查
}

// ==================== 事件类型常量 ====================

export const EventTypes = {
  // 游戏生命周期
  GAME_INITIALIZED: 'game:initialized',
  GAME_STARTED: 'game:started',
  GAME_ENDED: 'game:ended',
  GAME_PAUSED: 'game:paused',
  GAME_RESUMED: 'game:resumed',
  GAME_RESET: 'game:reset',

  // 游戏加载系统 (P0.4)
  GAME_LOADING_STARTED: 'game:loading:started',
  GAME_LOADING_PROGRESS: 'game:loading:progress',
  GAME_LOADING_COMPLETED: 'game:loading:completed',
  GAME_LOADING_FAILED: 'game:loading:failed',

  // 回合系统
  TURN_STARTED: 'turn:started',
  TURN_ENDED: 'turn:ended',
  TURN_PHASE_CHANGED: 'turn:phaseChanged',
  TURN_TIME_UPDATE: 'turn:timeUpdate',
  // 新增：回合内主动方（active agent）广播
  AGENT_TURN_STARTED: 'agent:turnStarted',
  AGENT_TURN_ENDED: 'agent:turnEnded',

  // 智能体生命周期
  AGENT_SPAWNED: 'agent:spawned',
  AGENT_ELIMINATED: 'agent:eliminated',
  AGENT_STATUS_CHANGED: 'agent:statusChanged',

  // 智能体行动
  AGENT_MOVED: 'agent:moved',
  AGENT_ACTION_STARTED: 'agent:actionStarted',
  AGENT_ACTION_COMPLETED: 'agent:actionCompleted',
  AGENT_ACTION_FAILED: 'agent:actionFailed',

  // 智能体属性
  AGENT_HEALTH_CHANGED: 'agent:healthChanged',
  AGENT_ATTRIBUTE_CHANGED: 'agent:attributeChanged',
  AGENT_INVENTORY_CHANGED: 'agent:inventoryChanged',

  // 战斗系统
  COMBAT_STARTED: 'combat:started',
  COMBAT_DAMAGE_DEALT: 'combat:damageDealt',
  COMBAT_ATTACK_MISSED: 'combat:attackMissed',
  COMBAT_CRITICAL_HIT: 'combat:criticalHit',
  COMBAT_ENDED: 'combat:ended',

  // 资源/库存系统 (统一ItemID)
  RESOURCE_SPAWNED: 'resource:spawned',
  RESOURCE_COLLECTED: 'resource:collected',
  // 新增：系统/状态类的资源消失事件（不等同“被玩家收集”）
  RESOURCE_DESPAWNED: 'resource:despawned',
  ITEM_USED: 'item:used',
  ITEM_CONSUMED: 'item:consumed',
  ITEM_DROPPED: 'item:dropped',
  WEAPON_EQUIPPED: 'weapon:equipped', // 🔥 新增：武器装备事件
  WEAPON_DURABILITY_CHANGED: 'weapon:durabilityChanged',
  WEAPON_BROKEN: 'weapon:broken',

  // 环境系统
  SAFEZONE_UPDATED: 'safeZone:updated',
  SAFEZONE_WARNING: 'safeZone:warning',
  SAFEZONE_CONTRACTION_STARTED: 'safeZone:contractionStarted',
  SAFEZONE_CONTRACTION_COMPLETED: 'safeZone:contractionCompleted',
  SAFEZONE_DAMAGE_APPLIED: 'safeZone:damageApplied',
  ENVIRONMENT_DAMAGE: 'environment:damage',
  MAP_GENERATED: 'map:generated',
  TERRAIN_CHANGED: 'terrain:changed',

  // LLM决策系统
  LLM_READY: 'llm:ready', // ✅ Phase 2: LLM初始化完成，系统就绪
  LLM_PATH_PLANNED: 'llm:pathPlanned',
  LLM_OPTIONS_GENERATED: 'llm:optionsGenerated',
  LLM_OPTION_EVALUATED: 'llm:optionEvaluated',
  LLM_DECISION_MADE: 'llm:decisionMade',
  LLM_STRATEGY_ANALYZED: 'llm:strategyAnalyzed',
  LLM_PERCEPTION_UPDATED: 'llm:perceptionUpdated',
  LLM_MEMORY_UPDATED: 'llm:memoryUpdated',
  LLM_ERROR_RECOVERED: 'llm:errorRecovered',
  // LLM决策链阶段事件
  LLM_STATE_ANALYSIS_COMPLETED: 'llm:stateAnalysisCompleted',
  LLM_OPTIONS_GENERATION_COMPLETED: 'llm:optionsGenerationCompleted',
  LLM_OPTIONS_EVALUATION_COMPLETED: 'llm:optionsEvaluationCompleted',
  LLM_FINAL_DECISION_COMPLETED: 'llm:finalDecisionCompleted',
  LLM_DECISION_CHAIN_COMPLETED: 'llm:decisionChainCompleted',

  // 系统管理
  SYSTEM_ERROR: 'system:error',
  SYSTEM_WARNING: 'system:warning',
  SYSTEM_STATE_SYNC: 'system:stateSync',
  SYSTEM_HEARTBEAT: 'system:heartbeat',
  SYSTEM_PERFORMANCE_METRIC: 'system:performanceMetric',

  // 用户交互
  UI_AGENT_SELECTED: 'ui:agentSelected',
  UI_CAMERA_CHANGED: 'ui:cameraChanged',
  UI_PLAYBACK_SPEED_CHANGED: 'ui:playbackSpeedChanged',
  UI_FILTER_CHANGED: 'ui:filterChanged',
  // UI diagnostics / overlays
  UI_HEATMAP: 'ui:heatmap',

  // 统计分析
  STATS_TURN_COMPLETE: 'stats:turnComplete',
  STATS_GAME_COMPLETE: 'stats:gameComplete',
  STATS_AGENT_PERFORMANCE: 'stats:agentPerformance',
  ANALYTICS_USER_ACTION: 'analytics:userAction'
} as const;

export type EventType = typeof EventTypes[keyof typeof EventTypes]; 

// ================ 新增事件数据结构 ==================

export interface AgentTurnStartedData {
  agentId: string;
  turnNumber: number;
  timestamp: string;
}

export interface AgentTurnEndedData {
  agentId: string;
  turnNumber: number;
  timestamp: string;
  success: boolean;
}

// ================ P0.4 游戏加载事件 ==================

export interface GameLoadingStartedData {
  roomId: string;
  totalStages: number;
  estimatedDuration: number; // milliseconds
  participantCount: number;
}

export interface GameLoadingProgressData {
  roomId: string;
  stage: 'room_init' | 'game_init' | 'agent_registration' | 'map_generation' | 'asset_preload';
  stageIndex: number; // 1-based index
  totalStages: number;
  percentage: number; // 0-100
  message: string;
}

export interface GameLoadingCompletedData {
  roomId: string;
  totalDuration: number; // milliseconds
  readyToStart: boolean;
}

export interface GameLoadingFailedData {
  roomId: string;
  stage: string;
  error: string;
  canRetry: boolean;
}

// Loading Event Type Aliases
export type GameLoadingStartedEvent = GameEvent<GameLoadingStartedData>;
export type GameLoadingProgressEvent = GameEvent<GameLoadingProgressData>;
export type GameLoadingCompletedEvent = GameEvent<GameLoadingCompletedData>;
export type GameLoadingFailedEvent = GameEvent<GameLoadingFailedData>;

// Game Lifecycle Event Type Aliases
export type GameStartedEvent = GameEvent<GameStartedData>;
export type GameEndedEvent = GameEvent<GameEndedData>;

// ==================== 🧠 LLM Decision Chain Event Data (新增) ====================

/**
 * LLM状态分析完成数据
 */
export interface LLMStateAnalysisCompletedData {
  agentId: string;
  requestId: string;
  analysis: {
    currentSituation: string;
    threats: string[];
    opportunities: string[];
    strategicPriority: string;
    confidence: number;
  };
  processingTime: number;
  timestamp: string;
}

/**
 * LLM选项生成完成数据
 */
export interface LLMOptionsGenerationCompletedData {
  agentId: string;
  requestId: string;
  options: Array<{
    id: string;
    type: string;
    description: string;
    estimatedCost: number;
  }>;
  optionCount: number;
  processingTime: number;
  timestamp: string;
}

/**
 * LLM选项评估完成数据
 */
export interface LLMOptionsEvaluationCompletedData {
  agentId: string;
  requestId: string;
  evaluatedOptions: Array<{
    id: string;
    score: number;
    pros: string[];
    cons: string[];
    riskLevel: string;
  }>;
  topOptionId: string;
  processingTime: number;
  timestamp: string;
}

/**
 * LLM最终决策完成数据
 */
export interface LLMFinalDecisionCompletedData {
  agentId: string;
  requestId: string;
  decisionId: string;
  chosenOption: {
    id: string;
    type: string;
    score: number;
  };
  reasoning: string;
  confidence: number;
  processingTime: number;
  timestamp: string;
}

/**
 * LLM决策链完成数据
 */
export interface LLMDecisionChainCompletedData {
  agentId: string;
  requestId: string;
  chainId: string;
  totalStages: number;
  completedStages: number;
  finalDecision: {
    id: string;
    type: string;
    confidence: number;
  };
  totalProcessingTime: number;
  stageTimings: {
    stateAnalysis: number;
    optionGeneration: number;
    optionEvaluation: number;
    finalDecision: number;
  };
  timestamp: string;
}
